import type { Preferences } from "@esi/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { api } from "../api.ts";
import { ok } from "../api-errors.ts";
import { currentLocale, type Locale, setLocale } from "../i18n/i18n.ts";
import { type Me, useMe } from "../me.ts";
import { useSession } from "../session.tsx";
import { type ThemeChoice, useTheme } from "../theme/theme.tsx";
import { accountKeys, csrf } from "./queries.ts";

/**
 * The account's saved language and theme follow the user to every browser: applied once the
 * session is known (the browser's own copy keeps painting the first frame right).
 */
export function useApplySavedPreferences(): void {
  const me = useMe();
  const { choice, setChoice } = useTheme();
  const applied = useRef<string | null>(null);
  const preferences = me.data?.preferences;
  useEffect(() => {
    if (!preferences || applied.current === me.data?.userId) {
      return;
    }
    applied.current = me.data?.userId ?? null;
    if (preferences.language && preferences.language !== currentLocale()) {
      void setLocale(preferences.language);
    }
    if (preferences.theme && preferences.theme !== choice) {
      setChoice(preferences.theme);
    }
  }, [preferences, me.data?.userId, choice, setChoice]);
}

/** Applies a language or theme at once and saves it to the account. */
export function useSavePreferences() {
  const { csrfToken } = useSession();
  const { setChoice } = useTheme();
  const queryClient = useQueryClient();
  const save = useMutation({
    mutationFn: async (json: Partial<Preferences>) =>
      ok(await api.api.account.preferences.$patch({ json }, csrf(csrfToken))),
    onSuccess: (preferences) => {
      queryClient.setQueryData<Me>(["me"], (me) => (me ? { ...me, preferences } : me));
      void queryClient.invalidateQueries({ queryKey: accountKeys.account });
    },
  });
  return {
    language: (locale: Locale) => {
      void setLocale(locale);
      save.mutate({ language: locale });
    },
    theme: (theme: ThemeChoice) => {
      setChoice(theme);
      save.mutate({ theme });
    },
    /** "Suggest feedback" (feedback-from-conversation D6): the agent's unprompted drafts. */
    suggestFeedback: (on: boolean) => save.mutateAsync({ suggestFeedback: on }),
    saving: save.isPending,
    error: save.error,
  };
}

/**
 * "Suggest feedback" alone (feedback-from-conversation D6), for a card under an answer: saves
 * the preference to the account, without the theme or language.
 */
export function useSuggestFeedback() {
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (on: boolean) =>
      ok(
        await api.api.account.preferences.$patch(
          { json: { suggestFeedback: on } },
          csrf(csrfToken),
        ),
      ),
    onSuccess: (preferences) => {
      queryClient.setQueryData<Me>(["me"], (me) => (me ? { ...me, preferences } : me));
      void queryClient.invalidateQueries({ queryKey: accountKeys.account });
    },
  });
}
